/**
 * PublicCoursesCataloguePage — public standalone course catalogue (Round 40 · Task 8).
 *
 * Lists every course with an empty programme_id and status ∈ {published, active}.
 * Free + paid tabs match the programme catalogue pattern. Anonymous visitors
 * can browse; the "Enrol" button routes signed-in students straight to the
 * course detail + payment flow already used for programme-bound courses.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import api from "../lib/api";
import { GraduationCap, ArrowRight, CurrencyNgn, Sparkle } from "@phosphor-icons/react";

export default function PublicCoursesCataloguePage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | paid | free

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filter === "paid") params.paid_only = true;
    if (filter === "free") params.free_only = true;
    api.get("/courses/catalogue", { params })
      .then((r) => setCourses(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-courses-catalogue">
      <PublicNav />

      <section className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-14">
          <p className="uppercase tracking-widest text-[10px] text-amber-400 font-bold mb-3 flex items-center gap-2">
            <Sparkle size={12} weight="fill" /> Standalone courses
          </p>
          <h1 className="text-3xl md:text-5xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Learn at your own pace.
          </h1>
          <p className="mt-3 text-sm md:text-base text-slate-300 max-w-2xl">
            Enrol in individual courses without committing to a full programme. Standalone courses use the same learning engine as our flagship programmes — progress tracking, quizzes, forums, badges — with the flexibility to pick just what you need.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="catalogue-filters">
          {[
            { k: "all", label: "All courses" },
            { k: "paid", label: "Paid courses" },
            { k: "free", label: "Free courses" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              className={`px-4 py-2 text-sm rounded-none border ${filter === t.k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700 hover:bg-white"}`}
              data-testid={`catalogue-filter-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Loading catalogue…</p>
        ) : courses.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-10 text-center bg-white">
            <GraduationCap size={40} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No standalone courses available right now — check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="catalogue-grid">
            {courses.map((c) => (
              <article key={c.id} className="border border-slate-200 bg-white p-5 flex flex-col" data-testid={`catalogue-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{c.code || "Course"}</p>
                  {c.is_free ? (
                    <span className="text-[10px] px-2 py-0.5 bg-green-100 text-green-800 uppercase tracking-widest font-bold">Free</span>
                  ) : c.fee ? (
                    <span className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-900 flex items-center gap-1 font-bold">
                      <CurrencyNgn size={11} weight="bold" />
                      {Number(c.fee).toLocaleString()}
                    </span>
                  ) : null}
                </div>
                <h3 className="text-lg font-bold text-slate-950 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {c.title}
                </h3>
                <p className="text-sm text-slate-600 line-clamp-3 flex-1">{c.description || c.short_description || "A standalone course from School Builder Lab Africa."}</p>
                <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                  <span>{c.enrollee_count || 0} enrolled</span>
                  {c.duration && <span>{c.duration}</span>}
                </div>
                <Link
                  to={`/courses/public/${c.id}`}
                  className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-800 hover:bg-blue-900 text-white text-sm rounded-none"
                  data-testid={`catalogue-view-${c.id}`}
                >
                  View course <ArrowRight size={14} weight="bold" />
                </Link>
              </article>
            ))}
          </div>
        )}
      </div>

      <PublicFooter />
    </div>
  );
}
